import Trip from '../Trip';
import SuggestionService from '../SuggestionService';
import { VehicleType } from '../types';
import {
    CabinClassRequest,
    type FlightLegDto,
    type FlightsMetaDto,
} from '@clients/FlightApi';
import { ReserveType } from '@utils/itinerary';

export default class Flight extends Trip {
    override serviceId = 0;
    /**
     * Time of airplane departure from origin, in 'hh:mm' format
     */
    override departureTime = '';
    override reserveType = ReserveType.Flight;

    /**
     * Time of airplane arrival to destination, in 'hh:mm' format
     */
    override arrivalTime = '';

    override stops = 0;

    override origin = '';

    override destination = '';

    description = '';

    id = '';

    /**
     * Indicates the flight type, 0 for charter, 1 for system and 2 for international.
     */
    type = 0;

    override departureDateTime = new Date(0);
    override arrivalDateTime = new Date(0);

    override vehicleType = VehicleType.Plane;

    override originCityName = '';

    override originCode = '';

    override originTerminal = '';

    override destinationCityName = '';

    override destinationCode = '';

    override destinationTerminal = '';

    override key = '';

    duration_ = 0;
    override showCityCodes = false;

    status = '';

    flightNumber = '';

    override serviceNumber = '0';

    override get duration(): number {
        return this.duration_;
    }

    constructor(
        leg: FlightLegDto,
        meta: FlightsMetaDto,
        index: number,
        isDomestic: boolean,
        public cabinClass = CabinClassRequest.Unknown as CabinClassRequest,
        public cabinClassDisplayName: string | null = null
    ) {
        super();

        if (!leg) {
            return;
        }
        this.technicalStops =
            leg?.TechnicalStops?.map(
                ({ DepartureDateTime, Airport, ArrivalDateTime }) => ({
                    message: `توقف فنی در فرودگاه ${Airport} از 
                    ${dayConfig.js(ArrivalDateTime).format(
                        formatter({
                            format: 'long',
                            includeWeekday: true,
                            includeYear: true,
                            includeTime: true,
                        })
                    )} 
                    تا 
                    ${dayConfig.js(DepartureDateTime).format(
                        formatter({
                            format: 'long',
                            includeWeekday: true,
                            includeYear: true,
                            includeTime: true,
                        })
                    )}`,
                })
            ) || [];
        const airline = leg.Airline?.IataCode
            ? leg.Airline
            : leg.OperatingAirline;
        this.carrierCode = airline?.IataCode || '';
        const title = airline?.PersianTitle || airline?.EnglishTitle || '';
        this.departureTime = dayConfig
            .gregory(leg.DepartureTime?.substring(0, 19))
            .format('HH:mm');
        this.arrivalTime = dayConfig
            .gregory(leg.ArrivalTime?.substring(0, 19))
            .format('HH:mm');
        this.corporationInfo = {
            logoUrl: this.getLogo(this.carrierCode),
            title,
            subTitle: leg.FlightNumber?.toString(),
            code: this.carrierCode,
        };

        if (
            leg.Airline?.IataCode &&
            leg.OperatingAirline &&
            leg.OperatingAirline.IataCode &&
            leg.OperatingAirline.IataCode !== leg.Airline?.IataCode
        ) {
            this.operatingWarning = `صدور بلیط این پرواز توسط ${
                leg.Airline?.PersianTitle || leg.Airline?.EnglishTitle
            } و پرواز توسط ${
                leg.OperatingAirline.PersianTitle ||
                leg.OperatingAirline.EnglishTitle
            } انجام می‌شود.`;
        }

        this.origin = leg.Origin + (!isDomestic ? ` (${leg.OriginCode})` : '');
        this.originCityName = leg.Origin || '';
        this.originCode = leg.OriginCode || '';

        this.destination =
            leg.Destination + (!isDomestic ? ` (${leg.DestinationCode})` : '');
        this.destinationCityName = leg.Destination || '';
        this.destinationCode = leg.DestinationCode || '';

        this.showCityCodes = !isDomestic;
        this.serviceNumber = leg.FlightNumber || '';
        this.departureDateTime = dayConfig
            .gregory(leg.DepartureTime?.substring(0, 19))
            .toDate();
        this.arrivalDateTime = dayConfig
            .gregory(leg.ArrivalTime?.substring(0, 19))
            .toDate();

        this.flightNumber = leg.FlightNumber || '';
        this.vehicleInfo = {
            title: 'هواپیما',
            type: leg.AirCraft?.PersianTitle || '',
            code: leg.AirplaneCode || '',
            description: leg.AirCraft?.PersianTitle || '',
        };

        this.id = leg.Id?.toString() || '';
        this.serviceId = leg.Id ?? 0;
        this.duration_ =
            leg.JourneyTime?.match(/(\d+):(\d+):(\d+)/)!
                .slice(1)
                .reduce(
                    (prev, val, index) =>
                        prev + parseInt(val) * 60 ** (2 - index),
                    0
                ) || 0;
        this.stops = leg.Stops || 0;
        this.key = leg.Id?.toString() || '';
        this.status = leg.FlightStatus || '';

        this.originStartPoint = leg.OriginAirport;
        this.destinationEndPoint = leg.DestinationAirport;

        const originAirport =
            leg.OriginAirport ||
            meta.RoutesInfo?.[index]?.Origin?.PersianTitle ||
            leg.Origin;

        const destinationAirport =
            leg.DestinationAirport ||
            meta.RoutesInfo?.[index]?.Destination?.PersianTitle ||
            leg.Destination;

        if (isDomestic) {
            this.originTerminal = `فرودگاه ${originAirport} (${
                this.originCode
            })${
                leg.DepartureTerminal
                    ? ' - ' + `ترمینال ${leg.DepartureTerminal}`
                    : ''
            }`;
            this.destinationTerminal = `فرودگاه ${destinationAirport} (${
                this.destinationCode
            })${
                leg.ArrivalTerminal
                    ? ' - ' + `ترمینال ${leg.ArrivalTerminal}`
                    : ''
            }`;
        } else {
            this.originTerminal = `${leg.OriginAirport} (${this.originCode})`;
            this.destinationTerminal = `${leg.DestinationAirport} (${this.destinationCode})`;
        }
        this.type = 1;
        this.suggestionServices =
            leg.SuggestedServices?.map(
                sug =>
                    new SuggestionService({
                        id: sug.Id || 0,
                        title: sug.Title || '',
                        description: sug.Description || '',
                        price: sug.Price || 0,
                        unitTitle: sug.UnitTitle || '',
                        needDescription: sug.NeedDescription,
                        image: sug.Images?.[0],
                        discount: sug.DiscountAmount || 0,
                        levelRange: sug.ApplicableLevelRange,
                        flightClass:
                            (sug.FlightClass as CabinClassRequest) ?? undefined,
                    })
            ) || [];
    }

    override get tripExtraInfo(): string[] {
        if (
            [CabinClassRequest.Unknown, CabinClassRequest.All].includes(
                this.cabinClass as any
            )
        ) {
            return super.tripExtraInfo;
        }
        const cabinClassName = this.cabinClassDisplayName || '';

        return [...super.tripExtraInfo, cabinClassName];
    }

    updateClass(
        cabinClass = CabinClassRequest.Unknown as CabinClassRequest,
        cabinClassDisplayName: string | null = null
    ) {
        this.cabinClass = cabinClass;
        this.cabinClassDisplayName = cabinClassDisplayName;
    }

    override get destinationHasWarning() {
        return false;
    }
}
